import React from 'react';
import { uniq } from 'lodash';
import { ManageUnitsUnitGroupEnumType } from '@mark43/cad-api';

/**
 * The minimal shape selection derivation needs. Any object with `id` and `manageUnitGroup`
 * satisfies it, keeping the pure helpers testable without a full `ManageUnitsUnitView`.
 */
export type SelectableUnit = {
    id: number;
    manageUnitGroup: ManageUnitsUnitGroupEnumType;
};

/**
 * The user's explicit on/off toggles — the ONLY persisted selection state. Everything else (system
 * selection, effective selection, released set) derives from it plus the current unit rows and the
 * recommendation id set.
 *
 * - `on`: ids the user explicitly selected (always wins, even if the system wouldn't select them).
 * - `off`: ids the user explicitly de-selected (always wins, even if the system would select them).
 *
 * The two sets are mutually exclusive — toggling an id into one removes it from the other.
 */
export type SelectionDeltas = {
    on: Set<number>;
    off: Set<number>;
};

const emptyDeltas: SelectionDeltas = { on: new Set(), off: new Set() };

/**
 * The system selection is a pure function of the visible rows and the recommendation id set: a row
 * is system-selected iff it is ALREADY_DISPATCHED, HELD, or RECOMMENDED-and-visible.
 *
 * Recommended membership is an id set (not read off the row) because the store owns the
 * recommendation source; already-dispatched/held are intrinsic to `manageUnitGroup`. A recommended
 * id with no matching row is never selected, and one whose row isn't in a system-selectable group
 * (e.g. UNAVAILABLE — assigned elsewhere / out of service) isn't selected either — membership is
 * gated on the row's current group, not just plan membership.
 *
 * This replaces a legacy nudge-path quirk (`fetchUnitsByIds`): the old reducer reset with the raw
 * `unitIdsAlreadyInResponsePlan`, auto-selecting plan units even when unavailable or filtered
 * off-screen, and only on nudges, not initial load. That flaky inconsistency is dropped in favor of
 * one deterministic rule.
 */
export function deriveSystemSelectedUnitIds(
    items: SelectableUnit[],
    recommendedUnitIds: Set<number>
): number[] {
    return items
        .filter(
            (unit) =>
                unit.manageUnitGroup === 'ALREADY_DISPATCHED' ||
                unit.manageUnitGroup === 'HELD' ||
                (unit.manageUnitGroup === 'RECOMMENDED' && recommendedUnitIds.has(unit.id))
        )
        .map((unit) => unit.id);
}

/**
 * The effective selection: system selection minus user de-selections, plus user selections. User
 * intent always wins over the system.
 */
export function deriveSelectedUnitIds(
    items: SelectableUnit[],
    recommendedUnitIds: Set<number>,
    deltas: SelectionDeltas
): number[] {
    const systemSelected = deriveSystemSelectedUnitIds(items, recommendedUnitIds);
    return uniq(systemSelected.filter((id) => !deltas.off.has(id)).concat([...deltas.on]));
}

/**
 * The released set: system-selected ids the user has explicitly de-selected — units the system
 * would otherwise keep selected but the user turned off (shown as a "released"/ghost pill).
 *
 * As `systemSelected ∩ deltas.off`, a recommended/assigned unit the user turns off stays in the
 * released set deterministically across `items`/`recommendedUnitIds` refreshes, as long as its row
 * stays in a system-selectable group. This fixes a legacy reducer bug: it dropped user-toggled ids
 * from its system set on every refresh, so the ghost pill flakily vanished.
 */
export function deriveReleasedUnitIds(
    items: SelectableUnit[],
    recommendedUnitIds: Set<number>,
    deltas: SelectionDeltas
): number[] {
    const systemSelected = deriveSystemSelectedUnitIds(items, recommendedUnitIds);
    return systemSelected.filter((id) => deltas.off.has(id));
}

type UseUnitSelectionArgs = {
    items: SelectableUnit[];
    recommendedUnitIds: Set<number>;
};

type UseUnitSelectionReturns = {
    selectedUnitIds: number[];
    toggle: (unitId: number) => void;
    releasedUnitIds: number[];
};

/**
 * Returns `next`, or the previous reference if it's element-wise equal — keeping downstream
 * `useMemo`/effect deps stable when derived ids are unchanged, even though
 * `items`/`recommendedUnitIds` are rebuilt every render by the MobX-observer caller. Plain React
 * state is naturally reference-stable; deriving from live store rows isn't, so we stabilize by
 * content.
 */
function useStableIds(next: number[]): number[] {
    const ref = React.useRef<number[]>(next);
    const prev = ref.current;
    const isSame = prev.length === next.length && prev.every((id, index) => id === next[index]);
    if (!isSame) {
        ref.current = next;
    }
    return ref.current;
}

export function useUnitSelection({
    items,
    recommendedUnitIds,
}: UseUnitSelectionArgs): UseUnitSelectionReturns {
    const [deltas, setDeltas] = React.useState<SelectionDeltas>(emptyDeltas);

    const selectedUnitIds = useStableIds(deriveSelectedUnitIds(items, recommendedUnitIds, deltas));
    const releasedUnitIds = useStableIds(deriveReleasedUnitIds(items, recommendedUnitIds, deltas));

    // Hold the latest derivation inputs in a ref so `toggle` reads current rows without recreating
    // itself every render — keeping `toggle` reference-stable for downstream memoized callbacks,
    // matching the former reducer's dispatch-backed toggler.
    const derivationInputRef = React.useRef({ items, recommendedUnitIds });
    derivationInputRef.current = { items, recommendedUnitIds };

    const toggle = React.useCallback((unitId: number) => {
        // Decide intent against the selection as it stands now: if the unit is currently selected,
        // the user wants it off; otherwise on. The two delta sets stay mutually exclusive.
        setDeltas((currentDeltas) => {
            const { items: currentItems, recommendedUnitIds: currentRecommendedUnitIds } =
                derivationInputRef.current;
            const isCurrentlySelected = deriveSelectedUnitIds(
                currentItems,
                currentRecommendedUnitIds,
                currentDeltas
            ).includes(unitId);
            const on = new Set(currentDeltas.on);
            const off = new Set(currentDeltas.off);
            if (isCurrentlySelected) {
                on.delete(unitId);
                off.add(unitId);
            } else {
                off.delete(unitId);
                on.add(unitId);
            }
            return { on, off };
        });
    }, []);

    return {
        selectedUnitIds,
        toggle,
        releasedUnitIds,
    };
}
